import styles from './MethodologyPage.module.css';

export function MethodologyPage() {
  return (
    <div className={styles.page}>
      <h1 className={styles.heading}>Methodology</h1>

      <section className={styles.section}>
        <h2>Data sources</h2>
        <p>
          Wallet balances and transaction history come from the{' '}
          <a href="https://blockstream.info" target="_blank" rel="noreferrer noopener">
            Blockstream Esplora API
          </a>
          , a public Bitcoin block explorer. BTC/USD pricing and historical chart data
          come from{' '}
          <a href="https://www.coingecko.com" target="_blank" rel="noreferrer noopener">
            CoinGecko
          </a>
          . WhaleTail does not run its own Bitcoin node or price oracle.
        </p>
      </section>

      <section className={styles.section}>
        <h2>Calculations</h2>
        <p>
          Balances are computed from Blockstream&apos;s funded and spent output sums,
          expressed in satoshis to avoid floating-point rounding error. Total balance is
          the confirmed balance plus any pending mempool adjustment. USD value uses the
          current BTC/USD price and is a live estimate, not a historical transaction-time
          value.
        </p>
      </section>

      <section className={styles.section}>
        <h2>Caching</h2>
        <p>
          Wallet summary and history responses are cached at the edge for roughly 30–60
          seconds with a five-minute stale-while-revalidate window. Market signal
          responses are cached for about two to five minutes. The browser also keeps the
          last successful wallet summary in local storage so a temporary source outage can
          still show recent data with an age indicator.
        </p>
      </section>

      <section className={styles.section}>
        <h2>EMA (Exponential Moving Average)</h2>
        <p>
          EMA weights recent prices more heavily than older ones. Each value is calculated
          as <code>price × k + previousEma × (1 − k)</code>, where{' '}
          <code>k = 2 / (period + 1)</code>. The first price in the series seeds the EMA.
          WhaleTail computes a fast EMA (default 12 hourly periods) and a slow EMA
          (default 48 hourly periods) over a 90-day price window.
        </p>
      </section>

      <section className={styles.section}>
        <h2>RSI (Relative Strength Index)</h2>
        <p>
          RSI measures the speed and magnitude of recent price changes on a 0–100 scale
          using Wilder-style smoothing of average gains and losses over a period (default
          14). A flat series with no gains or losses is reported as RSI 50 rather than
          being incorrectly treated as overbought.
        </p>
      </section>

      <section className={styles.section}>
        <h2>Decision matrix</h2>
        <pre className={styles.matrix}>{`emaFast > emaSlow:
  RSI >= overbought       -> UPTREND · OVERBOUGHT
  otherwise               -> UPTREND

emaFast < emaSlow:
  RSI <= oversold         -> DOWNTREND · OVERSOLD
  otherwise               -> DOWNTREND

insufficient trend data   -> NO SIGNAL`}</pre>
      </section>

      <section className={styles.section}>
        <h2>Limitations</h2>
        <ul>
          <li>Public providers can rate-limit, delay, or temporarily fail.</li>
          <li>
            Address labels are user-configurable and are not verified identity claims.
          </li>
          <li>
            EMA/RSI signals are backward-looking and can be wrong in fast-moving markets.
          </li>
          <li>
            WhaleTail tracks addresses, not full wallets that may span multiple addresses.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2>Not financial or betting advice</h2>
        <p>
          Every signal, model probability, simulated result, odds comparison, and
          price-gap flag shown in WhaleTail is an informational experiment. None of it is
          financial, trading, or betting advice, and most of these experiments have not
          shown a reliable edge. Never make financial or betting decisions based on this
          tool.
        </p>
      </section>
    </div>
  );
}
